import { useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import keycloakService from '../../../auth/keycloak-service';
import { getEnvironmentVariable } from '../../../utils';

/**
 * Hook for SignalR connection to messaging hub
 */
export const useSignalR = (conversationId: number | null) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const startConnection = async () => {
      try {
        // Get token from Keycloak
        if (!keycloakService.isAuthenticated()) {
          console.warn('User not authenticated, cannot connect to SignalR');
          return;
        }

        await keycloakService.updateToken(60);
        const token = keycloakService.getToken();

        if (!token) {
          console.warn('No token available, cannot connect to SignalR');
          return;
        }

        // Build SignalR connection URL
        // API_BASE_URL is like "https://api.local.mytruss.us" or "http://localhost:3000/api"
        const baseUrl =
          (getEnvironmentVariable('API_BASE_URL') as string) ?? 'http://localhost:3000/api';

        // Construct hub URL properly
        let hubUrl: string;
        try {
          // Parse the base URL to ensure we construct it correctly
          const url = new URL(baseUrl);

          // Remove /api from pathname if present, then add /hubs/messaging
          let pathname = url.pathname;
          if (pathname.endsWith('/api')) {
            pathname = pathname.replace(/\/api$/, '');
          }
          pathname = pathname.endsWith('/')
            ? `${pathname}hubs/messaging`
            : `${pathname}/hubs/messaging`;

          hubUrl = `${url.protocol}//${url.host}${pathname}`;

          console.log('SignalR Hub URL:', hubUrl);
        } catch (error) {
          console.error('Error constructing SignalR URL:', error, 'Base URL:', baseUrl);
          // Fallback: simple string replacement
          hubUrl = baseUrl.endsWith('/api')
            ? baseUrl.replace('/api', '/hubs/messaging')
            : `${baseUrl}/hubs/messaging`;
        }

        // Ensure we have a proper URL
        if (!hubUrl.startsWith('http')) {
          console.error('Invalid SignalR hub URL:', hubUrl, 'Base URL was:', baseUrl);
          return;
        }

        const newConnection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: async () => {
              // Refresh token if needed
              await keycloakService.updateToken(60);
              return keycloakService.getToken() || '';
            },
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: retryContext => {
              // Exponential backoff: 0s, 2s, 10s, 30s, then 30s
              if (retryContext.previousRetryCount === 0) return 0;
              if (retryContext.previousRetryCount === 1) return 2000;
              if (retryContext.previousRetryCount === 2) return 10000;
              return 30000;
            },
          })
          .configureLogging(LogLevel.Information)
          .build();

        // Set up message handler
        newConnection.on('MessageReceived', message => {
          // This will be handled by the component using this hook
          console.log('Message received via SignalR:', message);
        });

        // Handle connection events
        newConnection.onclose(error => {
          console.log('SignalR connection closed', error);
          setIsConnected(false);
        });

        newConnection.onreconnecting(error => {
          console.log('SignalR reconnecting', error);
          setIsConnected(false);
        });

        newConnection.onreconnected(connectionId => {
          console.log('SignalR reconnected', connectionId);
          setIsConnected(true);
          // Rejoin conversation group
          if (conversationId) {
            newConnection.invoke('JoinConversation', conversationId).catch(err => {
              console.error('Error rejoining conversation:', err);
            });
          }
        });

        // Start connection
        await newConnection.start();
        setIsConnected(true);
        setConnection(newConnection);
        connectionRef.current = newConnection;

        // Join conversation group
        await newConnection.invoke('JoinConversation', conversationId);
      } catch (error) {
        console.error('Error starting SignalR connection:', error);
        setIsConnected(false);
      }
    };

    startConnection();

    // Cleanup on unmount or conversation change
    return () => {
      if (connectionRef.current) {
        if (conversationId) {
          connectionRef.current.invoke('LeaveConversation', conversationId).catch(console.error);
        }
        connectionRef.current.stop().catch(console.error);
        connectionRef.current = null;
        setConnection(null);
        setIsConnected(false);
      }
    };
  }, [conversationId]);

  return { connection, isConnected };
};
